import type { FixtureCellData } from './constants';

// Mirrors the ids in FILTER_OPTIONS (useFixtureTickerFilters). Declared here
// rather than imported so the hook can depend on this module without a cycle.
export type FixtureValueMetric =
  | 'overall'
  | 'attack'
  | 'defence'
  | 'goals'
  | 'cleanSheets';

// Difficulty and clean-sheet probability are rates, so a range is summarised
// by its mean. Expected goals accumulate, so a range is summarised by its sum
// (matching the legacy ticker's GF column).
const SUMMED_METRICS: ReadonlySet<FixtureValueMetric> = new Set(['goals']);

const PERCENTAGE_METRICS: ReadonlySet<FixtureValueMetric> = new Set([
  'cleanSheets'
]);

const DECIMAL_PLACES = 1;
const PERCENT_SCALE = 100;
const MISSING_VALUE_PLACEHOLDER = '-';

export const SUMMARY_COLUMN_LABELS: Record<FixtureValueMetric, string> = {
  overall: 'Diff',
  attack: 'Att',
  defence: 'Def',
  goals: 'xG',
  cleanSheets: 'CS%'
};

export type VisibleGameweekRange = {
  gameweekStart: number;
  gameweekEnd: number;
  hiddenGameweeks: number[];
};

// The per-fixture number behind the colour band, in the unit of the selected
// metric. Null when the payload carries no inference for it, which is the case
// for unconfirmed fixtures and for the public preview endpoint's goal and
// clean-sheet figures.
export const getFixtureValue = (
  fixture: FixtureCellData,
  metric: FixtureValueMetric
): number | null => {
  const inferences = fixture.inferences;
  if (!inferences) return null;

  switch (metric) {
    case 'overall':
    case 'attack':
    case 'defence':
      return inferences.difficulty[metric];
    case 'goals':
      return inferences.predictions?.goalsFor ?? null;
    case 'cleanSheets':
      return inferences.probabilities.cleanSheet ?? null;
  }
};

export const summariseFixtureValues = (
  fixtures: FixtureCellData[],
  metric: FixtureValueMetric,
  range: VisibleGameweekRange
): number | null => {
  const hidden = new Set(range.hiddenGameweeks);
  const values = fixtures.flatMap((fixture) => {
    const isVisible =
      fixture.gameweek >= range.gameweekStart &&
      fixture.gameweek <= range.gameweekEnd &&
      !hidden.has(fixture.gameweek);
    if (!isVisible) return [];
    const value = getFixtureValue(fixture, metric);
    return value === null ? [] : [value];
  });

  if (values.length === 0) return null;

  const total = values.reduce((sum, value) => sum + value, 0);
  return SUMMED_METRICS.has(metric) ? total : total / values.length;
};

export const formatFixtureValue = (
  value: number | null,
  metric: FixtureValueMetric
): string => {
  if (value === null) return MISSING_VALUE_PLACEHOLDER;
  if (PERCENTAGE_METRICS.has(metric)) {
    return `${Math.round(value * PERCENT_SCALE)}%`;
  }
  return value.toFixed(DECIMAL_PLACES);
};
